import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { 
  Hand, X, Pause, Settings, Type, Eye,
  ChevronUp, ChevronDown, Power, ToggleLeft, ToggleRight,
  Globe, Loader2, Link2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { signLanguageEventBus } from '../utils/signLanguageSync';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animated GIFs for smooth sign language animations
const SIGN_GIFS = {
  neutral: '/sign-gifs/neutral.gif',
  hello: '/sign-gifs/hello.gif',
  love: '/sign-gifs/love.gif',
  pray: '/sign-gifs/pray.gif',
  thank: '/sign-gifs/thank.gif',
};

// Fallback static images (for fingerspelling)
const STATIC_FRAMES = {
  neutral: 'https://static.prod-images.emergentagent.com/jobs/71bf15c1-4803-46cc-a875-0a5ed50896b1/images/5f8b7271eff8d8bc9c059a2c52c1f43e433ea65c3e1885c72230d9f9e34cc4b7.png',
  point: 'https://static.prod-images.emergentagent.com/jobs/71bf15c1-4803-46cc-a875-0a5ed50896b1/images/30a11d8e2063a1314d8b292df55a10e6ba33dd3ebbcfe4dfb951770f7ec0b359.png',
};

// Map words to GIF poses
const WORD_TO_GIF = {
  hello: 'hello', hi: 'hello', welcome: 'hello', bye: 'hello', goodbye: 'hello', wave: 'hello', hey: 'hello',
  love: 'love', heart: 'love', care: 'love', peace: 'love', family: 'love', 'i love you': 'love',
  pray: 'pray', prayer: 'pray', god: 'pray', bless: 'pray', amen: 'pray', church: 'pray', faith: 'pray', please: 'pray',
  thank: 'thank', thanks: 'thank', grateful: 'thank', appreciate: 'thank', 'thank you': 'thank',
};

// Fingerspell hand shapes for display
const LETTER_SHAPES = {
  a: '✊', b: '🖐️', c: '👌', d: '☝️', e: '✊', f: '👌', g: '🤏', h: '✌️',
  i: '🤙', j: '🤙', k: '✌️', l: '🤟', m: '✊', n: '✊', o: '👌', p: '🤏',
  q: '🤏', r: '🤞', s: '✊', t: '✊', u: '✌️', v: '✌️', w: '🤟', x: '☝️',
  y: '🤙', z: '☝️'
};

// Sign durations in ms
const SIGN_DURATIONS = {
  hello: 1400,
  love: 1800,
  pray: 1500,
  thank: 1200,
  neutral: 500,
  fingerspell: 400, // per letter
};

export default function SignLanguageWidget() {
  const { i18n } = useTranslation();
  const location = useLocation();
  
  // Core state
  const [isOpen, setIsOpen] = useState(false);
  const [isEnabled, setIsEnabled] = useState(() => localStorage.getItem('signLanguageEnabled') === 'true');
  const [autoSign, setAutoSign] = useState(() => localStorage.getItem('signLanguageAutoSign') === 'true');
  const [syncWithBot, setSyncWithBot] = useState(() => localStorage.getItem('signLanguageSyncBot') !== 'false');
  const [signLanguage, setSignLanguage] = useState(() => localStorage.getItem('signLanguageType') || 'ASL');
  
  // Animation state
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const [currentGif, setCurrentGif] = useState(SIGN_GIFS.neutral);
  const [currentSign, setCurrentSign] = useState(null);
  const [currentLetter, setCurrentLetter] = useState('');
  const [useGif, setUseGif] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [gifKey, setGifKey] = useState(0); // Force GIF restart
  
  // Refs
  const timeoutRef = useRef(null);
  const queueRef = useRef([]);
  const lastPathRef = useRef(location.pathname);
  const isSyncingRef = useRef(false);
  
  const isSpanish = i18n.language === 'es';

  // Save preferences
  useEffect(() => {
    localStorage.setItem('signLanguageEnabled', isEnabled);
    localStorage.setItem('signLanguageAutoSign', autoSign);
    localStorage.setItem('signLanguageSyncBot', syncWithBot);
    localStorage.setItem('signLanguageType', signLanguage);
  }, [isEnabled, autoSign, syncWithBot, signLanguage]);

  // Listen for translator bot sync events
  useEffect(() => {
    if (!syncWithBot || !isEnabled) return;
    
    const unsubscribe = signLanguageEventBus.subscribe((event) => {
      if (event.type === 'SIGN_TEXT' && event.text) {
        isSyncingRef.current = true;
        playSignAnimation(event.text);
      } else if (event.type === 'STOP_SIGNING') {
        stopAnimation();
      }
    });
    
    return () => unsubscribe();
  }, [syncWithBot, isEnabled]);

  // Process sign queue - plays GIFs sequentially
  const processQueue = useCallback(() => {
    if (queueRef.current.length === 0) {
      setIsPlaying(false);
      setCurrentSign(null);
      setCurrentLetter('');
      setCurrentGif(SIGN_GIFS.neutral);
      setUseGif(true);
      isSyncingRef.current = false;
      return;
    }

    const sign = queueRef.current.shift();
    setCurrentSign(sign);

    if (sign.type === 'word_sign' && sign.gif) {
      // Play the animated GIF
      setCurrentLetter('');
      setUseGif(true);
      setGifKey(prev => prev + 1); // Force GIF to restart
      setCurrentGif(SIGN_GIFS[sign.gif] || SIGN_GIFS.neutral);
      
      // Move to next sign after GIF duration
      const duration = SIGN_DURATIONS[sign.gif] || 1000;
      timeoutRef.current = setTimeout(processQueue, duration);
    } else if (sign.type === 'fingerspell' && sign.letters) {
      // Show letters one by one with static frames
      setUseGif(false);
      let letterIndex = 0;
      
      const showLetter = () => {
        if (letterIndex < sign.letters.length) {
          const letter = sign.letters[letterIndex];
          setCurrentLetter(letter);
          setCurrentGif(letterIndex % 2 === 0 ? STATIC_FRAMES.neutral : STATIC_FRAMES.point);
          letterIndex++;
          timeoutRef.current = setTimeout(showLetter, SIGN_DURATIONS.fingerspell);
        } else {
          setCurrentLetter('');
          setUseGif(true);
          processQueue();
        }
      };
      showLetter();
    } else {
      // Pause between words
      setCurrentGif(SIGN_GIFS.neutral);
      timeoutRef.current = setTimeout(processQueue, 300);
    }
  }, []);

  // AI translation to sign language
  const translateToSignLanguage = useCallback(async (text) => {
    if (!text?.trim()) return null;
    setIsLoading(true);
    
    try {
      const response = await axios.post(`${API}/translator/sign-language`, {
        text: text.substring(0, 500),
        sign_language: signLanguage
      });
      return response.data;
    } catch (error) {
      console.error('Sign language translation error:', error);
      return createFallbackSigns(text);
    } finally {
      setIsLoading(false);
    }
  }, [signLanguage]);

  // Fallback sign creation when AI unavailable
  const createFallbackSigns = (text) => {
    const words = text.toLowerCase().split(/\s+/).slice(0, 15);
    const signs = [];
    
    for (const word of words) {
      const cleanWord = word.replace(/[^a-z]/g, '');
      if (!cleanWord) continue;
      
      if (WORD_TO_GIF[cleanWord]) {
        signs.push({
          word: cleanWord,
          type: 'word_sign',
          gif: WORD_TO_GIF[cleanWord],
          description: `Sign: ${cleanWord}`
        });
      } else if (cleanWord.length <= 6) {
        signs.push({
          word: cleanWord,
          type: 'fingerspell',
          letters: cleanWord.split(''),
          description: `Spell: ${cleanWord.toUpperCase()}`
        });
      }
      signs.push({ type: 'pause' });
    }
    
    return { signs, language: signLanguage };
  };

  // Main play function
  const playSignAnimation = useCallback(async (text) => {
    if (!text?.trim()) return;
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    setCurrentText(text);
    setIsPlaying(true);
    
    const signData = await translateToSignLanguage(text);
    
    if (signData?.signs?.length > 0) {
      // Map AI response to GIFs
      queueRef.current = signData.signs.map(sign => {
        if (sign.type === 'word_sign') {
          const gif = WORD_TO_GIF[sign.word?.toLowerCase()] || sign.pose;
          return { ...sign, gif: SIGN_GIFS[gif] ? gif : null };
        }
        return sign;
      }).filter(s => s.gif || s.type === 'fingerspell' || s.type === 'pause');
      
      if (queueRef.current.length > 0) {
        processQueue();
      } else {
        setIsPlaying(false);
      }
    } else {
      setIsPlaying(false);
    }
  }, [translateToSignLanguage, processQueue]);

  // Stop animation
  const stopAnimation = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    queueRef.current = [];
    setIsPlaying(false);
    setCurrentSign(null);
    setCurrentLetter('');
    setCurrentGif(SIGN_GIFS.neutral);
    setUseGif(true);
    isSyncingRef.current = false;
  }, []);

  // Auto-sign on page change
  useEffect(() => {
    if (autoSign && isEnabled && location.pathname !== lastPathRef.current) {
      lastPathRef.current = location.pathname;
      const timer = setTimeout(() => {
        const heading = document.querySelector('h1');
        if (heading?.textContent && !isSyncingRef.current) {
          playSignAnimation(heading.textContent);
        }
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [location.pathname, autoSign, isEnabled, playSignAnimation]);

  // Handlers
  const handleSignSelected = () => {
    const text = window.getSelection()?.toString().trim();
    if (text) playSignAnimation(text);
    else toast.info('Select some text first');
  };

  const handleSignHeading = () => {
    const heading = document.querySelector('h1');
    if (heading?.textContent) playSignAnimation(heading.textContent);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  if (!isEnabled) {
    return (
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        className="fixed bottom-24 left-6 z-40 p-3 bg-gradient-to-r from-purple to-burgundy rounded-full shadow-lg hover:shadow-xl transition-all group"
        onClick={() => setIsEnabled(true)}
        title="Enable Sign Language"
        data-testid="sign-language-enable"
      >
        <Hand size={24} className="text-white" />
      </motion.button>
    );
  }

  return (
    <>
      {/* Main Button */}
      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="fixed bottom-24 left-6 z-50">
        <div className="relative">
          <motion.button
            onClick={() => setIsOpen(!isOpen)}
            className={`p-4 rounded-full shadow-xl transition-all ${isOpen ? 'bg-burgundy' : 'bg-gradient-to-r from-purple to-burgundy'}`}
            animate={isPlaying ? { scale: [1, 1.05, 1] } : {}}
            transition={{ repeat: isPlaying ? Infinity : 0, duration: 0.5 }}
            data-testid="sign-language-toggle"
          >
            {isOpen ? <X size={28} className="text-white" /> : <Hand size={28} className="text-white" />}
          </motion.button>
          
          {/* Status indicators */}
          {(autoSign || syncWithBot) && !isOpen && (
            <div className="absolute -top-1 -right-1 flex gap-0.5">
              {autoSign && (
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                </div>
              )}
              {syncWithBot && (
                <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                  <Link2 size={8} className="text-white" />
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-44 left-6 z-50 w-80 bg-white rounded-2xl shadow-2xl border border-gold/20 overflow-hidden"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-purple to-burgundy p-3 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Hand size={18} />
                  <span className="font-semibold text-sm">{signLanguage} Sign Language</span>
                  <span className="text-xs bg-white/20 px-1.5 py-0.5 rounded">GIF</span>
                </div>
                <button onClick={() => setShowSettings(!showSettings)} className="p-1 hover:bg-white/20 rounded-full">
                  <Settings size={16} />
                </button>
              </div>
            </div>

            {/* Language Toggle */}
            <div className="flex border-b border-gold/20 text-xs">
              {['ASL', 'Libras'].map(lang => (
                <button
                  key={lang}
                  onClick={() => setSignLanguage(lang)}
                  className={`flex-1 py-2 font-medium transition-colors ${
                    signLanguage === lang ? 'bg-burgundy/10 text-burgundy border-b-2 border-burgundy' : 'text-charcoal/60'
                  }`}
                >
                  {lang === 'ASL' ? '🇺🇸' : '🇧🇷'} {lang}
                </button>
              ))}
            </div>

            {/* Controls Row */}
            <div className="px-3 py-2 border-b border-gold/10 flex gap-2">
              <button
                onClick={() => setAutoSign(!autoSign)}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-all ${
                  autoSign ? 'bg-green-100 text-green-700 border border-green-300' : 'bg-cream text-charcoal/60'
                }`}
              >
                <Power size={12} />
                Auto
                {autoSign ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              </button>
              
              <button
                onClick={() => setSyncWithBot(!syncWithBot)}
                className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-all ${
                  syncWithBot ? 'bg-blue-100 text-blue-700 border border-blue-300' : 'bg-cream text-charcoal/60'
                }`}
                title="Sync with Translator Bot"
              >
                <Link2 size={12} />
                Sync Bot
                {syncWithBot ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
              </button>
            </div>

            {/* Settings */}
            <AnimatePresence>
              {showSettings && (
                <motion.div
                  initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }}
                  className="bg-cream border-b border-gold/20 px-3 py-2 overflow-hidden"
                >
                  <button onClick={() => { setIsEnabled(false); setIsOpen(false); stopAnimation(); }} className="w-full text-xs text-red-600">
                    Disable Sign Language
                  </button>
                </motion.div>
              )}
            </AnimatePresence>

            {/* GIF Display */}
            <div className="p-3">
              <div className="w-full h-48 bg-gradient-to-br from-purple/5 to-burgundy/5 rounded-xl border border-gold/20 relative flex items-center justify-center overflow-hidden">
                {/* Loading */}
                {isLoading && (
                  <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10">
                    <Loader2 size={28} className="animate-spin text-burgundy" />
                  </div>
                )}
                
                {/* Animated GIF */}
                <img
                  key={useGif ? `gif-${gifKey}-${currentGif}` : `static-${currentGif}`}
                  src={currentGif}
                  alt="Sign Language Avatar"
                  className="h-full w-auto object-contain"
                  style={{ imageRendering: 'auto' }}
                />
                
                {/* Letter Badge */}
                {currentLetter && (
                  <motion.div
                    key={currentLetter}
                    initial={{ scale: 0, y: -10 }}
                    animate={{ scale: 1, y: 0 }}
                    className="absolute top-2 right-2 w-14 h-14 bg-gradient-to-br from-burgundy to-purple rounded-full flex flex-col items-center justify-center text-white shadow-lg"
                  >
                    <span className="text-xl font-bold">{currentLetter.toUpperCase()}</span>
                    <span className="text-lg">{LETTER_SHAPES[currentLetter] || '✋'}</span>
                  </motion.div>
                )}
                
                {/* Status */}
                {isPlaying && (
                  <motion.div 
                    className="absolute top-2 left-2 flex items-center gap-1 bg-green-500 text-white text-xs px-2 py-1 rounded-full"
                    animate={{ opacity: [1, 0.7, 1] }}
                    transition={{ repeat: Infinity, duration: 1 }}
                  >
                    <motion.span 
                      className="w-2 h-2 bg-white rounded-full"
                      animate={{ scale: [1, 1.5, 1] }}
                      transition={{ repeat: Infinity, duration: 0.5 }}
                    />
                    {isSyncingRef.current ? '🔗 Syncing' : '✋ Signing'}
                  </motion.div>
                )}
              </div>
              
              {/* Sign description */}
              {currentSign?.description && (
                <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-charcoal/70 text-center mt-2 bg-cream/50 rounded p-1.5 italic">
                  {currentSign.description}
                </motion.p>
              )}
              
              {/* Current text */}
              {currentText && (
                <p className="text-xs text-charcoal/80 mt-1 text-center bg-purple/5 rounded p-1.5 truncate">"{currentText}"</p>
              )}
            </div>

            {/* Actions */}
            <div className="px-3 pb-2 space-y-1.5">
              {isPlaying ? (
                <button onClick={stopAnimation} className="w-full px-3 py-2 bg-red-500 text-white rounded-xl text-sm flex items-center justify-center gap-2">
                  <Pause size={16} /> Stop
                </button>
              ) : (
                <>
                  <button onClick={handleSignSelected} className="w-full px-3 py-1.5 bg-burgundy text-white rounded-lg text-sm flex items-center justify-center gap-1">
                    <Type size={14} /> Sign Selected
                  </button>
                  <button onClick={handleSignHeading} className="w-full px-3 py-1.5 bg-gold text-charcoal rounded-lg text-sm flex items-center justify-center gap-1">
                    <Eye size={14} /> Sign Title
                  </button>
                </>
              )}
            </div>

            {/* Quick Signs with GIFs */}
            <div className="px-3 pb-2">
              <p className="text-xs text-charcoal/50 mb-1">Animated Signs:</p>
              <div className="flex flex-wrap gap-1">
                {[
                  { text: 'Hello', emoji: '👋' },
                  { text: 'Thank you', emoji: '🙏' },
                  { text: 'I love you', emoji: '🤟' },
                  { text: 'God bless', emoji: '✨' },
                  { text: 'Pray', emoji: '🙏' },
                ].map(item => (
                  <button
                    key={item.text}
                    onClick={() => playSignAnimation(item.text)}
                    className="px-2 py-0.5 bg-cream text-charcoal text-xs rounded-full hover:bg-purple/20 border border-gold/20 flex items-center gap-1"
                  >
                    {item.emoji} {item.text}
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="bg-cream px-3 py-1.5 border-t border-gold/10">
              <p className="text-xs text-charcoal/50 text-center flex items-center justify-center gap-1">
                <Globe size={10} /> Animated GIFs • {syncWithBot && <><Link2 size={10} /> Bot Sync</>}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
